import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { useApi } from "../contexts/ApiContext";
import { resolveEditorProjectRoute } from "../router/appRouteUtils.js";

const VersionControlContext = createContext(null);

export const useVersionControl = () => {
  const context = useContext(VersionControlContext);
  if (!context) {
    throw new Error("useVersionControl must be used within a VersionControlProvider");
  }
  return context;
};

export const VersionControlProvider = ({ children }) => {
  const location = useLocation();
  const [version, setVersionState] = useState({
    branchId: null,
    commitId: null
  });
  const [isTimeTravel, setIsTimeTravel] = useState(false);
  const [projectLoaded, setProjectLoaded] = useState(false);
  const api = useApi();
  const branchCacheRef = useRef(new Map());

  const setVersion = useCallback((branchId, commitId) => {
    setVersionState({ branchId, commitId });
  }, []);

  // Parse URL on change
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const branchId = params.get("branchId");
    const commitId = params.get("commitId");

    if (branchId || commitId) {
      setVersion(branchId, commitId);
    }
  }, [location.search, setVersion]);

  // A save can create the user's first branch, so refresh the ownership cache
  // before the saved Version becomes the selected Version.
  useEffect(() => {
    const invalidateBranchCache = () => {
      const projectId = resolveEditorProjectRoute(location.pathname)?.projectKey;

      if (projectId) {
        branchCacheRef.current.delete(projectId);
      }
    };

    api.addListener("project-saved", invalidateBranchCache);
    return () => {
      api.removeListener("project-saved", invalidateBranchCache);
    };
  }, [api, location.pathname]);

  // Determine Time Travel State
  useEffect(() => {
    if (!projectLoaded) return;

    const checkTimeTravel = async () => {
      const projectId = resolveEditorProjectRoute(location.pathname)?.projectKey;

      if (!projectId || projectId === "new" || projectId === "tutorial") {
        setIsTimeTravel(false);
        return;
      }

      try {
        const authUser = api.getAuthUser();
        if (!authUser) return;

        // Loading content canonicalizes branch/commit IDs. Cache branch ownership
        // by project so that synchronization does not cause a second branches call.
        let branches = branchCacheRef.current.get(projectId);
        if (!branches) {
          branches = await api.getProjectBranches(projectId);
          branchCacheRef.current.set(projectId, branches);
        }
        const userBranch = branches.find(b => b.user?.id === authUser.id || b.user_id === authUser.id);

        if (!userBranch) {
          // If user has no branch, they haven't made any saves yet.
          // They can't be "time traveling" from their own work since they have none.
          // So we treat this as NOT time travel.
          setIsTimeTravel(false);
          return;
        }

        const isUserBranch = version.branchId === userBranch.id;

        if (!isUserBranch) {
          // Rule 1: Not user's branch
          setIsTimeTravel(true);
          return;
        }

        // Rule 2: In user's branch, check if latest
        if (!version.commitId) {
          // If no commitId is specified, we assume we are at HEAD of the branch.
          setIsTimeTravel(false);
          return;
        }

        // Fetch Commits to find latest
        const commits = await api.getProjectBranchCommits(projectId, userBranch.id);
        const sortedCommits = Array.isArray(commits.data) ? commits.data : Array.isArray(commits) ? commits : [];
        // Sort descending
        sortedCommits.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        const latestCommit = sortedCommits[0];
        const isLatest = latestCommit && latestCommit.id === version.commitId;

        setIsTimeTravel(!isLatest);
      } catch (error) {
        console.error("Error checking time travel state:", error);
      }
    };

    checkTimeTravel();
  }, [api, location.pathname, version.branchId, version.commitId, projectLoaded]);

  const isReadOnly = !!version.commitId;

  const value = {
    currentBranchId: version.branchId,
    currentCommitId: version.commitId,
    isReadOnly,
    isTimeTravel,
    setVersion,
    projectLoaded,
    setProjectLoaded
  };

  return <VersionControlContext.Provider value={value}>{children}</VersionControlContext.Provider>;
};

VersionControlProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const withVersionControl = Component => {
  return function VersionControlComponent(props) {
    return (
      <VersionControlContext.Consumer>
        {context => <Component {...props} versionControl={context} />}
      </VersionControlContext.Consumer>
    );
  };
};
